import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Clock, AlertCircle } from "lucide-react";

// ── Cron parser ──────────────────────────────────────────────

const PRESETS = [
    { label: "Every minute", cron: "* * * * *" },
    { label: "Every 5 min", cron: "*/5 * * * *" },
    { label: "Hourly", cron: "0 * * * *" },
    { label: "Daily midnight", cron: "0 0 * * *" },
    { label: "Daily 9 AM", cron: "0 9 * * *" },
    { label: "Weekly Mon", cron: "0 0 * * 1" },
    { label: "Monthly 1st", cron: "0 0 1 * *" },
    { label: "Yearly", cron: "0 0 1 1 *" },
];

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function describeField(field: string, unit: string, names?: string[]): string {
    if (field === "*") return `every ${unit}`;
    if (field.includes("/")) {
        const [base, step] = field.split("/");
        return base === "*"
            ? `every ${step} ${unit}s`
            : `every ${step} ${unit}s starting at ${unit} ${base}`;
    }
    if (field.includes(",")) {
        const vals = field.split(",").map((v) => (names ? names[parseInt(v)] || v : v));
        return `at ${unit} ${vals.join(", ")}`;
    }
    if (field.includes("-")) {
        const [start, end] = field.split("-");
        const s = names ? names[parseInt(start!)] || start : start;
        const e = names ? names[parseInt(end!)] || end : end;
        return `${unit} ${s} through ${e}`;
    }
    const val = names ? names[parseInt(field)] || field : field;
    return `at ${unit} ${val}`;
}

function parseCron(expr: string): { description: string; error?: string } {
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) {
        return { description: "", error: `Expected 5 fields, got ${parts.length}` };
    }

    const [minute, hour, dom, month, dow] = parts;

    // Validate each field
    const fieldPattern = /^(\*|[0-9]+(-[0-9]+)?(,[0-9]+(-[0-9]+)?)*(\/[0-9]+)?|\*\/[0-9]+)$/;
    for (const field of parts) {
        if (!fieldPattern.test(field!)) {
            return { description: "", error: `Invalid field: "${field}"` };
        }
    }

    const segments: string[] = [];
    segments.push(describeField(minute!, "minute"));
    segments.push(describeField(hour!, "hour"));
    if (dom !== "*") segments.push(describeField(dom!, "day-of-month"));
    if (month !== "*") segments.push(describeField(month!, "month", MONTHS));
    if (dow !== "*") segments.push(describeField(dow!, "day-of-week", DAYS));

    // Build human-readable
    let desc = segments.join(", ");
    desc = desc.charAt(0).toUpperCase() + desc.slice(1);

    return { description: desc };
}

function getNextRuns(expr: string, count: number): Date[] {
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) return [];

    const results: Date[] = [];
    const now = new Date();
    const check = new Date(now);
    check.setSeconds(0, 0);
    check.setMinutes(check.getMinutes() + 1);

    const matchesField = (value: number, field: string): boolean => {
        if (field === "*") return true;
        if (field.includes("/")) {
            const [base, step] = field.split("/");
            const stepN = parseInt(step!);
            if (base === "*") return value % stepN === 0;
            return value >= parseInt(base!) && (value - parseInt(base!)) % stepN === 0;
        }
        if (field.includes(",")) return field.split(",").some((v) => parseInt(v) === value);
        if (field.includes("-")) {
            const [s, e] = field.split("-");
            return value >= parseInt(s!) && value <= parseInt(e!);
        }
        return parseInt(field) === value;
    };

    let iterations = 0;
    while (results.length < count && iterations < 525960) { // max ~1 year of minutes
        iterations++;
        const m = check.getMinutes();
        const h = check.getHours();
        const d = check.getDate();
        const mo = check.getMonth() + 1;
        const dw = check.getDay();

        if (
            matchesField(m, parts[0]!) &&
            matchesField(h, parts[1]!) &&
            matchesField(d, parts[2]!) &&
            matchesField(mo, parts[3]!) &&
            matchesField(dw, parts[4]!)
        ) {
            results.push(new Date(check));
        }

        check.setMinutes(check.getMinutes() + 1);
    }

    return results;
}

// ── Component ────────────────────────────────────────────────

export function CronParser() {
    const [cron, setCron] = useState("0 9 * * 1-5");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        document.title = "Cron Expression Parser — Human-Readable Cron — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free cron expression parser. Convert cron syntax to human-readable descriptions. See next scheduled run times. Supports standard 5-field cron syntax.",
            );
        }
    }, []);

    const result = useMemo(() => parseCron(cron), [cron]);
    const nextRuns = useMemo(() => getNextRuns(cron, 5), [cron]);
    const fields = cron.trim().split(/\s+/);

    const copy = async () => {
        await navigator.clipboard.writeText(result.description || cron);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Cron Parser</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Cron Expression Parser</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Parse cron expressions into plain English. See the next scheduled run times and use presets for common patterns.
                    </p>
                </div>

                {/* Cron input */}
                <div className="mb-6">
                    <div className="flex items-center bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden">
                        <span className="px-4 text-zinc-600"><Clock size={16} /></span>
                        <input
                            value={cron}
                            onChange={(e) => setCron(e.target.value)}
                            placeholder="* * * * *"
                            spellCheck={false}
                            className="flex-1 bg-transparent py-4 text-[18px] text-zinc-200 font-mono outline-none placeholder:text-zinc-800 tracking-wider"
                        />
                        <button onClick={copy} className="px-4 text-zinc-600 hover:text-zinc-300 transition-colors">
                            {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                        </button>
                    </div>
                </div>

                {/* Field labels */}
                <div className="grid grid-cols-5 gap-2 mb-6 max-w-md">
                    {["Minute", "Hour", "Day (month)", "Month", "Day (week)"].map((label, i) => (
                        <div key={label} className="text-center">
                            <div className={`text-[16px] font-mono py-2 rounded-lg border ${fields[i] ? "border-white/[0.06] bg-[#111113] text-zinc-300" : "border-transparent text-zinc-700"}`}>
                                {fields[i] || "—"}
                            </div>
                            <div className="text-[10px] text-zinc-600 mt-1.5 leading-tight">{label}</div>
                        </div>
                    ))}
                </div>

                {/* Description */}
                {result.error ? (
                    <div className="flex items-center gap-2 text-[14px] text-red-400 mb-6 p-4 bg-red-400/[0.05] border border-red-400/10 rounded-xl">
                        <AlertCircle size={16} /> {result.error}
                    </div>
                ) : (
                    <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-5 mb-6">
                        <div className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider mb-2">Description</div>
                        <p className="text-[16px] text-zinc-200 leading-relaxed">{result.description}</p>
                    </div>
                )}

                {/* Presets */}
                <div className="flex flex-wrap gap-2 mb-8">
                    {PRESETS.map(({ label, cron: c }) => (
                        <button
                            key={c}
                            onClick={() => setCron(c)}
                            className={`px-3 py-1.5 rounded-lg text-[12px] border transition-colors ${cron === c ? "border-violet-400/30 bg-violet-400/10 text-violet-400" : "border-white/[0.06] text-zinc-600 hover:text-zinc-400"}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Next runs */}
                {nextRuns.length > 0 && (
                    <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden mb-16">
                        <div className="px-4 py-2.5 border-b border-white/[0.04]">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Next 5 runs</span>
                        </div>
                        {nextRuns.map((date, i) => (
                            <div key={i} className={`flex items-center justify-between px-4 py-3 text-[13px] ${i < nextRuns.length - 1 ? "border-b border-white/[0.04]" : ""}`}>
                                <span className="text-zinc-500">{date.toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}</span>
                                <span className="text-zinc-300 font-mono">{date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* SEO */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">What is a cron expression?</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>A cron expression is a string of five fields that defines a schedule. The fields represent minute, hour, day of month, month, and day of week. Cron is used in Unix-like operating systems to schedule recurring tasks — from running backups to sending reports.</p>
                        <p>Common special characters include * (any), / (step), - (range), and , (list). For example, "0 9 * * 1-5" means "at 9:00 AM, Monday through Friday."</p>
                        <p>Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/unix-timestamp" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Unix Timestamp Converter</Link>
                        <Link to="/tools/regex-tester" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Regex Tester</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
